import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import { AnswerResult, AnswerSource } from '../../models/answer-result.model';
import { displayHost } from '../../utils/url';
import { Block, Inline, parseBlocks, parseInline } from '../../utils/markdown-lite';

interface SourceView {
  /** 1-based position in `sources`: what claims refer to. */
  number: number;
  source: AnswerSource;
}

interface ClaimView {
  statement: Inline[];
  /** Each backing URL, with its number when it's one of the sources. */
  refs: { url: string; number: number | null; label: string; title: string | null }[];
}

/** What each response key means, for the hover tips (wording follows the
 *  API docs' `answer_result` / `structured_answer` / `answer_source` schemas). */
const FIELD_TIPS = {
  answer: 'The answer in prose, written by the model from the retrieved passages.',
  confidence: "The model's own estimate of how sure it is, from 0 to 1.",
  claims: 'The individual statements the answer rests on, each with the articles that back it.',
  claimSource: 'An article backing this claim (claims[].source_urls). Click to open it.',
  question: 'The question as it was received, trimmed.',
  mode: 'How the answer was arrived at: direct (one retrieval) or reasoning (several).',
  outputType: 'unstructured = prose only; structured = prose, confidence and claims.',
  sources: 'The articles the passages came from, one entry per article.',
  searches: 'Reasoning mode: the searches it ran, in order, and how many passages each found and added.',
  title: 'Title of the article. Click to open it.',
  domainName: 'The site the article was published on.',
  publishedAt: 'When the source published the article.',
} as const;

/** Cikisi passage ids the model cites, e.g. "[19168406]". They can't be
 *  resolved to a source yet (the response carries no id → source map), so
 *  they're left out of the prose here; the JSON tab keeps them. Five-plus
 *  digits, so "[2024]" or "[3]" in the text stay. */
const PASSAGE_ID_REF = /\s*\[\d{5,}\]/g;

const PAGE_SIZE = 10;

@Component({
  selector: 'app-answer-results',
  standalone: false,
  templateUrl: './answer-results.html',
  styleUrl: './answer-results.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnswerResults {
  readonly result = input.required<AnswerResult>();

  /** The answer, or the articles it was written from. */
  protected readonly view = signal<'answer' | 'sources'>('answer');
  protected readonly visible = signal(PAGE_SIZE);
  /** Show only sources from these domains (Top sites); empty = all. */
  protected readonly siteFilter = signal<ReadonlySet<string>>(new Set());
  protected readonly tips = FIELD_TIPS;

  /** The prose as paragraphs/lists, with the unresolvable passage ids taken out. */
  protected readonly blocks = computed<Block[]>(() => {
    const text = this.result().text;
    return text ? parseBlocks(text.replace(PASSAGE_ID_REF, '')) : [];
  });

  private readonly numbered = computed<SourceView[]>(() =>
    this.result().sources.map((source, i) => ({ number: i + 1, source })));

  protected readonly claims = computed<ClaimView[]>(() => {
    const structured = this.result().structured;
    if (!structured) return [];
    const byUrl = new Map(this.numbered().map(s => [s.source.url, s]));
    return structured.claims.map(claim => ({
      statement: parseInline(claim.statement),
      refs: claim.sourceUrls.map(url => {
        const hit = byUrl.get(url);
        return {
          url,
          number: hit?.number ?? null,
          label: hit?.source.host ?? displayHost(null, url),
          title: hit?.source.title ?? null,
        };
      }),
    }));
  });

  /** Number the sources only when claims point at them by number. */
  protected readonly showNumbers = computed(() => this.claims().some(c => c.refs.some(r => r.number !== null)));

  /** Numbers of the sources the claims cite. */
  private readonly cited = computed(() => new Set(
    this.claims().flatMap(c => c.refs.map(r => r.number)).filter((n): n is number => n !== null),
  ));

  protected readonly citedCount = computed(() => this.cited().size);

  /** Cited sources first (in number order), then the rest as the API listed them. */
  protected readonly orderedSources = computed(() => {
    const cited = this.cited();
    if (!cited.size) return this.numbered();
    const all = this.numbered();
    return [...all.filter(s => cited.has(s.number)), ...all.filter(s => !cited.has(s.number))];
  });

  protected readonly filteredSources = computed(() => {
    const sites = this.siteFilter();
    return sites.size ? this.orderedSources().filter(s => sites.has(s.source.host)) : this.orderedSources();
  });

  protected readonly shownSources = computed(() => this.filteredSources().slice(0, this.visible()));

  /** Why there's no answer, in the docs' terms (see `answer_result.answer`). */
  protected readonly noAnswerReason = computed(() => {
    const r = this.result();
    if (r.text || r.structured?.claims.length) return null;
    if (r.meta.answerParsed === false) return "The model replied, but its reply couldn't be read as structured JSON.";
    if (!r.sources.length) return 'Nothing was retrieved for this question.';
    return 'The model returned nothing for this question.';
  });

  protected toggleSite(host: string): void {
    this.siteFilter.update(set => {
      const next = new Set(set);
      if (next.has(host)) next.delete(host); else next.add(host);
      return next;
    });
    this.view.set('sources');
    this.visible.set(PAGE_SIZE);
  }

  protected clearSites(): void {
    this.siteFilter.set(new Set());
    this.visible.set(PAGE_SIZE);
  }

  protected showMore(): void {
    this.visible.update(n => n + PAGE_SIZE);
  }
}

